import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { hasClass } from '@utility/class-name';
import {
  Button,
  Checkbox as KitCheckbox,
  cn,
  Popover,
  Switch,
  Tooltip,
} from '@skupreme/shared/ui';
import { toPlacement } from '@utility/popup-placement';
import { TableHeaderToolTip } from '@components/TableHeaderToolTip';
import { tooltipTexts } from '@utility/constants/common';
import type {
  IBadgeProps,
  IInfoToolTipPopupProps,
  IMCFLabelProps,
  IStyledToggleProps,
  ITabLabelWithCountProps,
  ITitleHeaderBarProps,
} from 'types/common/elements';
import type React from 'react';

/*
 * Text and box primitives, on Tailwind. Colours stay the legacy theme's so
 * nothing shifts; they live in LEGACY so a later move to kit tokens is one
 * edit each. Every primitive also carries a stable marker class (`mute-text`
 * and so on) for the styled-components rules that used to target it as
 * `${MuteText}`.
 */
const LEGACY = {
  black: 'sk:text-fg',
  grey: 'sk:text-fg-tertiary',
  danger: 'sk:text-danger',
  yellow: 'sk:text-warning',
  link: 'sk:text-link',
  blueContrast: 'sk:text-[#2BCFFE]',
  pink: 'sk:text-[#F15BB5]',
  pinkContrast: 'sk:text-[#F3A9D6]',
  purple: 'sk:text-[#67388F]',
  success: 'sk:text-success',
  greenContrast: 'sk:text-[#3EDB52]',
  green: 'sk:text-brand',
} as const;

type SpanProps = HTMLAttributes<HTMLSpanElement>;
type DivProps = HTMLAttributes<HTMLDivElement>;

function span(displayName: string, marker: string, classes: string) {
  const Text = forwardRef<HTMLSpanElement, SpanProps>(
    ({ className, ...rest }, ref) => (
      <span {...rest} ref={ref} className={cn(marker, classes, className)} />
    ),
  );
  Text.displayName = displayName;
  return Text;
}

function div(displayName: string, marker: string, classes: string) {
  const Block = forwardRef<HTMLDivElement, DivProps>(
    ({ className, ...rest }, ref) => (
      <div {...rest} ref={ref} className={cn(marker, classes, className)} />
    ),
  );
  Block.displayName = displayName;
  return Block;
}

export const PageHeaderText = span(
  'PageHeaderText',
  'page-header-text',
  cn(
    'sk:text-base sk:leading-[19px] sk:font-bold',
    LEGACY.black,
    'sk:[&_.text-small]:text-sm sk:[&_.text-small]:leading-[14px] sk:[&_.text-small]:font-bold',
    'sk:[&_.text-small_.ui.checkbox]:[scale:1]',
  ),
);

export const PageHeaderBlackText = span(
  'PageHeaderBlackText',
  'page-header-black-text',
  cn('sk:text-[13px] sk:leading-[18px] sk:font-bold', LEGACY.black),
);

export const HeaderText = span(
  'HeaderText',
  'header-text',
  cn('sk:text-xl sk:leading-5 sk:font-bold sk:whitespace-nowrap', LEGACY.black),
);

export const TitleBarTitle = span(
  'TitleBarTitle',
  'title-bar-title',
  'sk:text-[15px] sk:leading-[23px] sk:whitespace-nowrap',
);

export const PrimaryText = span('PrimaryText', 'primary-text', LEGACY.black);

export const MuteText = forwardRef<
  HTMLSpanElement,
  SpanProps & { fontSize?: string }
>(({ fontSize: _fontSize, className, ...rest }, ref) => (
  <span
    {...rest}
    ref={ref}
    className={cn('mute-text', LEGACY.grey, className)}
  />
));
MuteText.displayName = 'MuteText';

export const DangerText = span('DangerText', 'danger-text', LEGACY.danger);
export const YellowText = span('YellowText', 'yellow-text', LEGACY.yellow);
export const BlueText = span('BlueText', 'blue-text', LEGACY.link);
export const BlueDarkText = span(
  'BlueDarkText',
  'blue-dark-text',
  LEGACY.blueContrast,
);
export const PinkText = span('PinkText', 'pink-text', LEGACY.pink);
export const PinkDarkText = span(
  'PinkDarkText',
  'pink-dark-text',
  LEGACY.pinkContrast,
);
export const PurpleText = span('PurpleText', 'purple-text', LEGACY.purple);
export const SuccessText = span('SuccessText', 'success-text', LEGACY.success);
export const SuccessDarkText = span(
  'SuccessDarkText',
  'success-dark-text',
  LEGACY.greenContrast,
);
export const ActiveText = span('ActiveText', 'active-text', LEGACY.black);
export const AccentText = span('AccentText', 'accent-text', LEGACY.green);

interface IColorTextProps extends SpanProps {
  color?: string;
}

/** Text in any colour, black by default. */
export const ColorText = forwardRef<HTMLSpanElement, IColorTextProps>(
  ({ color, style, className, ...rest }, ref) => (
    <span
      {...rest}
      ref={ref}
      className={cn('color-text', className)}
      style={{ color: color || 'black', ...style }}
    />
  ),
);
ColorText.displayName = 'ColorText';

export const BoldText = forwardRef<
  HTMLSpanElement,
  SpanProps & { strong?: boolean; href?: string }
>(({ strong: _strong, className, ...rest }, ref) => (
  <span
    {...rest}
    ref={ref}
    className={cn('bold-text sk:font-bold', className)}
  />
));
BoldText.displayName = 'BoldText';

export const ProminentText = span(
  'ProminentText',
  'prominent-text',
  'sk:text-base sk:font-bold',
);

export const CardHeaderText = span(
  'CardHeaderText',
  'card-header-text',
  'sk:text-xl sk:font-bold',
);

export const PopupHeader = span(
  'PopupHeader',
  'popup-header',
  'sk:text-sm sk:leading-[15px] sk:font-bold',
);

export const Border = div(
  'Border',
  'common-border',
  'sk:my-[2em] sk:h-px sk:bg-primary!',
);

export const MuteBorder = div(
  'MuteBorder',
  'mute-border',
  'sk:my-[2em] sk:h-px sk:bg-fill-tertiary!',
);

export const BoldHeaderText = span(
  'BoldHeaderText',
  'bold-header-text',
  cn(
    'sk:text-base sk:leading-[19px] sk:font-bold',
    LEGACY.black,
    'sk:[&_.text-small]:text-sm sk:[&_.text-small]:leading-[14px] sk:[&_.text-small]:font-bold',
  ),
);

export const Box = div(
  'Box',
  'common-box',
  'sk:rounded-[5px] sk:border-[0.5px] sk:border-solid sk:border-fg sk:p-[1em]',
);

export const BoxText = div(
  'BoxText',
  'box-text',
  cn('sk:text-[15px] sk:leading-[18px] sk:font-bold', LEGACY.black),
);

export const BoxSecondaryText = span(
  'BoxSecondaryText',
  'box-secondary-text',
  cn('sk:text-xs sk:leading-[14px] sk:font-bold', LEGACY.black),
);

export const ShadowBox = div(
  'ShadowBox',
  'shadow-box',
  cn(
    'sk:rounded-[5px] sk:bg-white! sk:p-[1.3em] sk:shadow-[0_2px_15px_rgba(57,57,72,0.05)]',
    'sk:[&.flat-box>div]:z-0',
  ),
);

export const OnboardShadowBox = div(
  'OnboardShadowBox',
  'onboard-shadow-box',
  cn(
    'sk:rounded-[10px] sk:bg-white sk:p-[1.3em] sk:transition-all sk:duration-200 sk:ease-linear',
    'sk:shadow-[0_5px_25px_rgba(57,57,72,0.05)] sk:hover:shadow-[0_5px_25px_rgba(57,57,72,0.2)]',
  ),
);

/* Sticky to the scrolling card, rather than fixed and offset by the sidebar. */
const HEADER_BAR = cn(
  'sk:sticky sk:top-0 sk:h-[67px] sk:w-full sk:px-[5px] sk:py-[14px] sk:transition-all sk:duration-200',
  'sk:border-0 sk:border-b-[0.5px] sk:border-solid sk:border-border',
  'sk:[&.tab-layout]:border-b-0',
);

type HeaderBarProps = DivProps & {
  zIndex?: number;
  darkBackground?: boolean;
};

export const StyledHeaderBar = forwardRef<HTMLDivElement, HeaderBarProps>(
  ({ zIndex, darkBackground, className, style, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'styled-header-bar',
        HEADER_BAR,
        darkBackground ? 'sk:bg-fill-tertiary' : 'sk:bg-inherit',
        className,
      )}
      style={{ zIndex: zIndex || 997, ...style }}
    />
  ),
);
StyledHeaderBar.displayName = 'StyledHeaderBar';

export const HeaderBar = forwardRef<HTMLDivElement, ITitleHeaderBarProps>(
  (props, ref) => <StyledHeaderBar {...props} ref={ref} />,
);
HeaderBar.displayName = 'HeaderBar';

export const GuestHeaderBar = forwardRef<HTMLDivElement, HeaderBarProps>(
  ({ className, ...rest }, ref) => (
    <StyledHeaderBar
      {...rest}
      ref={ref}
      className={cn('sk:left-4 sk:w-full', className)}
    />
  ),
);
GuestHeaderBar.displayName = 'GuestHeaderBar';

export const SecondaryHeaderContainer = forwardRef<
  HTMLDivElement,
  DivProps & { bgColor?: string; zIndex?: number }
>(({ bgColor, zIndex: _zIndex, className, style, ...rest }, ref) => {
  const dark = hasClass(className, 'dark-bg');
  return (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'secondary-header-container',
        'sk:sticky sk:top-0 sk:z-[998] sk:flex sk:justify-end sk:px-[15px] sk:py-[5px]',
        'sk:[&.dark-bg]:w-full sk:[&.full-width]:w-full',
        className,
      )}
      style={dark ? { backgroundColor: bgColor || '#FAFAFA', ...style } : style}
    />
  );
});
SecondaryHeaderContainer.displayName = 'SecondaryHeaderContainer';

export const ToolbarContainer = div(
  'ToolbarContainer',
  'toolbar-container',
  'sk:sticky sk:top-0 sk:z-[999] sk:flex sk:h-14 sk:w-full sk:flex-wrap sk:justify-end sk:gap-2 sk:bg-white sk:px-4',
);

export const BodyContainer = div(
  'BodyContainer',
  'body-container',
  cn('sk:px-4 sk:pt-0', 'sk:[&.tab-layout]:px-0 sk:[&.no-header]:pt-2.5'),
);

const FLEX_ROW = 'sk:flex sk:items-center';

export const FlexRow = forwardRef<
  HTMLDivElement,
  DivProps & { textAlign?: string }
>(({ textAlign: _textAlign, className, ...rest }, ref) => (
  <div
    {...rest}
    ref={ref}
    className={cn('flex-row-common', FLEX_ROW, className)}
  />
));
FlexRow.displayName = 'FlexRow';

export const CenterFlexRow = div(
  'CenterFlexRow',
  'flex-row-common center-flex-row',
  cn(FLEX_ROW, 'sk:justify-center'),
);

export const FlexColumn = div(
  'FlexColumn',
  'flex-row-common flex-column-common',
  'sk:flex sk:flex-col sk:items-center',
);

export const JustifyBetweenRow = div(
  'JustifyBetweenRow',
  'flex-row-common justify-between-row',
  cn(FLEX_ROW, 'sk:justify-between'),
);

export const BlurBackground = div(
  'BlurBackground',
  'blur-background',
  'sk:absolute sk:left-0 sk:z-[11] sk:h-screen sk:w-[83vw] sk:bg-fill-tertiary sk:opacity-50',
);

export const DetailSubHeader = span(
  'DetailSubHeader',
  'detail-sub-header',
  cn(
    'sk:flex sk:w-1/2 sk:items-center sk:justify-start sk:px-0 sk:text-sm sk:leading-4',
    LEGACY.grey,
  ),
);

export const DetailSubHeaderValue = span(
  'DetailSubHeaderValue',
  'detail-sub-header-value',
  'sk:flex sk:w-1/2 sk:items-center sk:justify-end sk:px-0 sk:text-sm sk:font-medium',
);

const BADGE = cn(
  'sk:ml-[5px] sk:flex sk:h-[15px] sk:min-w-[15px] sk:items-center sk:justify-center sk:rounded-[3px] sk:px-0.5',
  "sk:font-['Nunito_Sans'] sk:text-[10px] sk:leading-[10px] sk:font-normal sk:text-white",
);

export const BadgeContainer = span(
  'BadgeContainer',
  'badge-container',
  cn(BADGE, 'sk:bg-danger-solid'),
);

const BadgeSecondaryContainer = span(
  'BadgeSecondaryContainer',
  'badge-container',
  cn(BADGE, 'sk:bg-fg-tertiary'),
);

/** A count as a small red tag, capped at 10+; nothing for zero. */
export function BadgeDanger({ count }: IBadgeProps) {
  if (!count) {
    return null;
  }

  return <BadgeContainer>{count > 10 ? '10+' : count}</BadgeContainer>;
}

export function BadgeSecondary({ count }: IBadgeProps) {
  if (!count) {
    return null;
  }

  return (
    <BadgeSecondaryContainer>
      {count > 10 ? '10+' : count}
    </BadgeSecondaryContainer>
  );
}

/**
 * The kit Switch behind the Semantic toggle's API: `onChange(event, data)`
 * gets `{ checked }` in both places, as the old callers read it from either.
 * `className="danger"` turns the track red when on.
 */
export function StyledToggle({
  checked,
  defaultChecked,
  disabled,
  readOnly,
  name,
  id,
  className,
  label,
  labelProps,
  onChange,
  'aria-label': ariaLabel,
  'data-field-value': dataFieldValue,
}: IStyledToggleProps) {
  return (
    <div
      className={cn('sk:flex! sk:items-center', className)}
      data-field-value={dataFieldValue}
    >
      <Switch
        id={id}
        name={name}
        isSelected={checked}
        defaultSelected={defaultChecked}
        isDisabled={disabled}
        isReadOnly={readOnly}
        tone={hasClass(className, 'danger') ? 'danger' : 'primary'}
        aria-label={ariaLabel}
        onChange={isSelected => {
          const data = { checked: isSelected, name };
          onChange?.({ target: data }, data);
        }}
      >
        {label ? <span {...labelProps}>{label}</span> : undefined}
      </Switch>
    </div>
  );
}

type CheckboxChange = {
  checked: boolean;
  name?: string;
  value?: string;
  indeterminate?: boolean;
};

export interface IStyledCheckboxProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'onClick'> {
  checked?: boolean;
  defaultChecked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  value?: string;
  /** Lands on the input, so an outside `<label htmlFor>` still toggles it. */
  id?: string;
  label?: ReactNode;
  'aria-label'?: string;
  /**
   * Called with the new state, Semantic-style: `(event, { checked })`. The
   * event is only `{ target }`, not a React event: no caller stops or
   * prevents it, and this type keeps any new one from trying.
   */
  onChange?: (event: { target: CheckboxChange }, data: CheckboxChange) => void;
  /** Semantic called both on a toggle; some callers use this one. */
  onClick?: (event: { target: CheckboxChange }, data: CheckboxChange) => void;
}

/**
 * The kit Checkbox behind the Semantic checkbox's API. Outside props (a
 * Popup's hover handlers, data attributes) land on the wrapper, which is
 * what a Popup trigger needs.
 */
export const StyledCheckbox = forwardRef<HTMLDivElement, IStyledCheckboxProps>(
  (
    {
      checked,
      defaultChecked,
      indeterminate,
      disabled,
      readOnly,
      name,
      value,
      id,
      label,
      'aria-label': ariaLabel,
      onChange,
      onClick,
      className,
      ...rest
    },
    ref,
  ) => (
    <div
      {...rest}
      ref={ref}
      className={cn('checkbox-holder sk:relative sk:flex', className)}
    >
      <KitCheckbox
        id={id}
        name={name}
        value={value}
        isSelected={checked}
        defaultSelected={defaultChecked}
        isIndeterminate={indeterminate}
        isDisabled={disabled}
        isReadOnly={readOnly}
        aria-label={ariaLabel}
        onChange={isSelected => {
          /* What Semantic's callback data carried that callers might read. */
          const data = { checked: isSelected, name, value, indeterminate };
          onChange?.({ target: data }, data);
          onClick?.({ target: data }, data);
        }}
      >
        {label}
      </KitCheckbox>
    </div>
  ),
);
StyledCheckbox.displayName = 'StyledCheckbox';

/** A checkbox centred in a table cell. */
export const StyledCheckboxTable = forwardRef<
  HTMLDivElement,
  IStyledCheckboxProps
>(({ className, ...props }, ref) => (
  <StyledCheckbox
    {...props}
    ref={ref}
    className={cn('sk:mx-auto sk:w-fit', className)}
  />
));
StyledCheckboxTable.displayName = 'StyledCheckboxTable';

const WARNING = cn(
  'sk:mb-5 sk:flex sk:items-center sk:justify-between sk:rounded-[5px] sk:px-5 sk:py-3',
  'sk:transition-all sk:duration-200 sk:hover:opacity-85 sk:max-[768px]:mt-[50px]',
  'sk:[&_h3]:mx-0 sk:[&_h3]:my-[7px] sk:[&_h3]:text-white! sk:[&_span]:mx-0 sk:[&_span]:my-[7px] sk:[&_span]:text-white!',
  'sk:[&_.pointer]:cursor-pointer sk:[&>div]:flex sk:[&>div]:items-center sk:[&_button>span]:text-xs',
);

export const OrangeWarning = div(
  'OrangeWarning',
  'orange-warning',
  cn(WARNING, 'sk:bg-caution-solid'),
);

export const YellowWarning = div(
  'YellowWarning',
  'orange-warning yellow-warning',
  cn(WARNING, 'sk:bg-warning-solid'),
);

export const GreenWarning = div(
  'GreenWarning',
  'orange-warning green-warning',
  cn(WARNING, 'sk:bg-brand'),
);

interface IOrderTabLabelProps {
  text: React.ReactNode | string;
  count: number;
}

function OrderTabLabel({ text, count }: IOrderTabLabelProps) {
  return (
    <>
      {text}{' '}
      {!!count && <MuteText className="sk:text-[15px]!">{count}</MuteText>}
    </>
  );
}

export function MCFLabel({ count = 0 }: IMCFLabelProps) {
  return (
    <TableHeaderToolTip
      label={<OrderTabLabel text="MCF" count={count} />}
      helpText={tooltipTexts.mcf}
    />
  );
}

export function MFLabel({ count = 0 }: IMCFLabelProps) {
  return (
    <TableHeaderToolTip
      label={<OrderTabLabel text="MF" count={count} />}
      helpText={tooltipTexts.mf}
    />
  );
}

export function FBALabel({ count = 0 }: IMCFLabelProps) {
  return (
    <TableHeaderToolTip
      label={<OrderTabLabel text="FBA" count={count} />}
      helpText={tooltipTexts.fba}
    />
  );
}

export function BatchOrdersLabel({ count = 0 }: IMCFLabelProps) {
  return (
    <TableHeaderToolTip
      label={<OrderTabLabel text="Batch Order" count={count} />}
      helpText={tooltipTexts.batchOrders}
    />
  );
}

export function TabLabelWithCount({
  text,
  count = 0,
  tooltipText,
}: ITabLabelWithCountProps) {
  return (
    <TableHeaderToolTip
      label={<OrderTabLabel text={text} count={count} />}
      helpText={tooltipText}
    />
  );
}

/**
 * The grey "i" icon; hovering or focusing it shows `title`. A `hoverable`
 * one holds a link, which a tooltip cannot, so it opens a popover on click.
 */
export function InfoTooltipPopup({
  title,
  position,
  imgClass,
  hoverable,
}: IInfoToolTipPopupProps) {
  const placement = toPlacement(position);
  const icon = (
    <img
      className={cn('sk:ml-1! sk:cursor-pointer', imgClass)}
      alt="More info"
      src="/images/icon-info.svg"
    />
  );

  if (hoverable) {
    return (
      <Popover
        aria-label="More info"
        placement={placement}
        className="sk:max-w-80 sk:p-3 sk:text-dense"
        trigger={
          <Button
            variant="ghost"
            shape="circle"
            size="sm"
            aria-label="More info"
          >
            {icon}
          </Button>
        }
      >
        {title}
      </Popover>
    );
  }

  return (
    <Tooltip
      content={title}
      placement={placement}
      triggerClassName="sk:align-middle"
    >
      {icon}
    </Tooltip>
  );
}

const BADGE_BLOCK =
  'sk:mb-[5px] sk:max-w-fit sk:rounded-[3px] sk:px-[5px] sk:py-[3px] sk:text-[11px] sk:text-white';

export const PrimaryBadge = div(
  'PrimaryBadge',
  'primary-badge',
  cn(BADGE_BLOCK, 'sk:bg-primary'),
);

export const GreyBadge = div(
  'GreyBadge',
  'primary-badge grey-badge',
  cn(
    BADGE_BLOCK,
    'sk:bg-[rgba(172,173,182,0.2)] sk:px-[3px] sk:py-0 sk:text-[10px] sk:text-black',
  ),
);

export const DangerBadge = div(
  'DangerBadge',
  'primary-badge danger-badge',
  cn(BADGE_BLOCK, 'sk:bg-danger-solid'),
);

export const DangerBanner = div(
  'DangerBanner',
  'danger-banner',
  'sk:mb-5 sk:max-w-fit sk:rounded-[5px] sk:bg-danger-solid sk:px-5 sk:py-2.5 sk:text-sm sk:text-white',
);
